import type { WebLocale } from '@/enums/web-locale';
import type { RouteConfig } from 'vue-router/types/router';

import { CollectionType } from '@/enums/collection-type';
import PressPage from '@/pages/PressPage.vue';
import TvtimeImporterPage from '@/pages/TvtimeImporterPage.vue';
import { RefreshToken, StaticPage } from '@/lazy-load-components';

export function makeStaticRoutes(currentLocale: WebLocale): RouteConfig[] {
	return [
		makePseudonymIdRoute(currentLocale),
		makeTvRoute(currentLocale),
		makeTvRegistrationRoute(currentLocale),
		makeGlobalPrivacyPolicyRoute(currentLocale),
		makePrivacyPolicyRoute(currentLocale),
		makeTermsZendeskRoute(currentLocale),
		makeFaqRoute(currentLocale),
		makeApplicationsRoute(currentLocale),
		makeAboutRoute(currentLocale),
		makeTalentRoute(currentLocale),
		makeImprintRoute(currentLocale),
		makePressRoute(currentLocale),
		makeRefreshTokenRoute(),
		makeStreamingAPIRoute(currentLocale),
		makeServiceIntegrationRoute(),
		makeTvTimeImporterRoute(currentLocale),
	];
}

// pseudonym-ID redirect - global to locale
function makePseudonymIdRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.pseudonymId',
		path: `/${currentLocale}/pseudonym-ID`,
		component: StaticPage,
		meta: {
			hasFilterBar: false,
		},
	};
}

// tv registration redirect to local
function makeTvRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.tv',
		path: '/tv',
		meta: {
			hasFilterBar: false,
		},
		redirect: to => {
			return `${currentLocale}${to.path}`;
		},
	};
}

// tv registration redirect to local
function makeTvRegistrationRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.tv.code',
		path: '/tv/:code',
		props: true,
		meta: {
			hasFilterBar: false,
		},
		redirect: to => {
			return `${currentLocale}${to.path}`;
		},
	};
}

function makeGlobalPrivacyPolicyRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.privacy.global',
		path: '/privacy-policy',
		meta: {
			hasFilterBar: false,
		},
		redirect: to => {
			// country specific slugs
			const countryMap: any = {
				hk: '私隱政策',
				tw: '私隱政策',
			};

			if (countryMap[currentLocale]) {
				return `${currentLocale}/${countryMap[currentLocale]}`;
			}

			return `${currentLocale}${to.path}`;
		},
	};
}

// Privacy Policy Zendesk redirect
function makePrivacyPolicyRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.privacy.zendesk',
		path: `/${currentLocale}/privacy-policy`,
		beforeEnter(to) {
			if (process.client) {
				location.href = `https://www.${JW_CONFIG.DOMAIN}${to.fullPath}`;
				return;
			}
		},
		meta: {
			reload: true,
		},
	};
}

// Terms Zendesk redirect
function makeTermsZendeskRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.terms.zendesk',
		path: `/${currentLocale}/terms`,
		beforeEnter(to) {
			if (process.client) {
				location.href = `https://www.${JW_CONFIG.DOMAIN}${to.fullPath}`;
				return;
			}
		},
		meta: {
			reload: true,
		},
	};
}

// FAQ
function makeFaqRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.faq',
		path: `/${currentLocale}/faq`,
		beforeEnter(to) {
			if (process.client) {
				location.href = `https://www.${JW_CONFIG.DOMAIN}${to.fullPath}`;
				return;
			}
		},
		meta: {
			reload: true,
		},
	};
}

// Apps
function makeApplicationsRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.apps',
		path: `/${currentLocale}/apps`,
		alias: [
			`/${currentLocale}/aplicaciones`,
			`/${currentLocale}/Apps`,
			`/${currentLocale}/applications`,
			`/${currentLocale}/のアプリ`,
		],
		component: StaticPage,
	};
}

// About
function makeAboutRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.about',
		path: `/${currentLocale}/about`,
		alias: [
			`/${currentLocale}/about-us`,
			`/${currentLocale}/ueber-uns`,
			`/${currentLocale}/quienes-somos`,
			`/${currentLocale}/a-propos`,
			`/${currentLocale}/sobre-nos`,
		],
		component: StaticPage,
	};
}

// Talent
function makeTalentRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.talent',
		path: `/${currentLocale}/talent`,
		alias: [`/${currentLocale}/Talent`],
		component: StaticPage,
	};
}

// Imprint
function makeImprintRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.imprint',
		path: `/${currentLocale}/imprint`,
		alias: [
			`/${currentLocale}/Impressum`,
			`/${currentLocale}/aviso-legal`,
			`/${currentLocale}/infos-legales`,
			`/${currentLocale}/出版說明`,
		],
		component: StaticPage,
	};
}

function makePressRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.press',
		path: `/${currentLocale}/press`,
		alias: [`/${currentLocale}/presse`, `/${currentLocale}/prensa`, `/${currentLocale}/imprensa`],
		component: PressPage,
		meta: {
			tab: 'static',
			collectionType: CollectionType.TITLELIST,
		},
	};
}

// Refresh Token
function makeRefreshTokenRoute(): RouteConfig {
	return {
		name: 'app.refreshtoken',
		path: '/refresh',
		component: RefreshToken,
	};
}

function makeStreamingAPIRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.streamingapi',
		path: `/${currentLocale}/JustWatch-Streaming-API`,
		component: StaticPage,
	};
}

function makeServiceIntegrationRoute(): RouteConfig {
	return {
		name: 'app.serviceintegration',
		path: '/us/service-integration',
		component: StaticPage,
	};
}

// TVTime Importer
function makeTvTimeImporterRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.tvtimeImporter',
		path: `/${currentLocale}/tvtime-importer`,
		component: TvtimeImporterPage,
		meta: {
			hasFilterBar: false,
		},
	};
}
